import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Settings as SettingsIcon,
  Globe,
  Activity,
  AlertTriangle,
  Upload,
} from 'lucide-react';
import { adminApi, type AdminUserDetail } from '../../lib/api';
import { LoadingSkeleton } from '../../components/admin/LoadingSkeleton';

type Tab = 'overview' | 'worlds' | 'settings' | 'danger';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;
      try {
        const data = await adminApi.getUser(id);
        setUser(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const refetchUser = async () => {
    if (!id) return;
    const data = await adminApi.getUser(id);
    setUser(data);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="card" />
        <LoadingSkeleton variant="card" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-ember mb-4">{error}</div>
        <Link to="/admin/users" className="text-rune hover:underline">
          Back to Users
        </Link>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-2 text-mist hover:text-parchment hover:bg-stone/30 rounded-lg transition-colors"
            aria-label="Back to users"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-display text-xl sm:text-2xl text-parchment">{user.email}</h1>
            <div className="flex gap-2 mt-1">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  user.role === 'admin' ? 'bg-rune/20 text-rune' : 'bg-stone/30 text-mist'
                }`}
              >
                {user.role}
              </span>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  user.status === 'active'
                    ? 'bg-success/20 text-success'
                    : user.status === 'waitlisted'
                      ? 'bg-rune/20 text-rune'
                      : user.status === 'suspended'
                        ? 'bg-ember/20 text-ember'
                        : 'bg-stone/30 text-mist'
                }`}
              >
                {user.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        <div className="border-b border-stone/30 min-w-max">
          <nav className="flex gap-1">
            <TabButton
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
              icon={Activity}
            >
              Overview
            </TabButton>
            <TabButton
              active={activeTab === 'worlds'}
              onClick={() => setActiveTab('worlds')}
              icon={Globe}
            >
              Worlds
            </TabButton>
            <TabButton
              active={activeTab === 'settings'}
              onClick={() => setActiveTab('settings')}
              icon={SettingsIcon}
            >
              Settings
            </TabButton>
            <TabButton
              active={activeTab === 'danger'}
              onClick={() => setActiveTab('danger')}
              icon={AlertTriangle}
            >
              Danger Zone
            </TabButton>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab user={user} />}

      {activeTab === 'worlds' && <WorldsTab user={user} onUpdate={refetchUser} />}

      {activeTab === 'settings' && <SettingsTab user={user} onUpdate={refetchUser} />}

      {activeTab === 'danger' && <DangerZoneTab user={user} onReset={refetchUser} />}
    </div>
  );
}

// Tab Button Component
function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3.5 sm:py-3 border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'border-rune text-rune'
          : 'border-transparent text-mist hover:text-parchment hover:border-stone/50'
      }`}
    >
      <Icon size={18} className="opacity-70" />
      <span className="text-sm font-medium">{children}</span>
    </button>
  );
}

// ========== OVERVIEW TAB ==========
function OverviewTab({ user }: { user: AdminUserDetail }) {
  const accountAgeDays = Math.floor(
    (Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24),
  );

  const totalLetters = user.worlds.reduce(
    (sum, w) => sum + w.lettersInCount + w.lettersOutCount,
    0,
  );

  const activeWorlds = user.worlds.filter((w) => {
    if (!w.lastInboundAt) return false;
    const daysSinceActivity = Math.floor(
      (Date.now() - new Date(w.lastInboundAt).getTime()) / (1000 * 60 * 60 * 24),
    );
    return daysSinceActivity < 7;
  }).length;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          <div className="text-mist text-sm mb-1">Total Worlds</div>
          <div className="text-parchment text-2xl font-display">{user.worlds.length}</div>
        </div>
        <div className="bg-night border border-success/30 rounded-xl p-4">
          <div className="text-mist text-sm mb-1">Active Worlds</div>
          <div className="text-parchment text-2xl font-display">{activeWorlds}</div>
          {user.worlds.length > 0 && (
            <div className="text-success text-xs mt-1">
              {((activeWorlds / user.worlds.length) * 100).toFixed(0)}% of total
            </div>
          )}
        </div>
        <div className="bg-night border border-rune/30 rounded-xl p-4">
          <div className="text-mist text-sm mb-1">Total Letters</div>
          <div className="text-parchment text-2xl font-display">{totalLetters}</div>
        </div>
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          <div className="text-mist text-sm mb-1">Daily Quota</div>
          <div className="text-parchment text-2xl font-display">{user.dailyLetterQuota}</div>
        </div>
      </div>

      {/* Account Information */}
      <div className="bg-night border border-stone/30 rounded-xl p-6">
        <h3 className="text-lg font-medium text-parchment mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-mist mb-1">User ID</div>
            <div className="text-parchment font-mono text-xs">{user.id}</div>
          </div>
          <div>
            <div className="text-mist mb-1">Email</div>
            <div className="text-parchment">{user.email}</div>
          </div>
          <div>
            <div className="text-mist mb-1">Verified Sender</div>
            <div className="text-parchment">{user.verifiedSenderEmail ?? 'None'}</div>
          </div>
          <div>
            <div className="text-mist mb-1">Daily Quota</div>
            <div className="text-parchment">
              {user.dailyLetterQuota} letter
              {user.dailyLetterQuota !== 1 ? 's' : ''}/day
            </div>
          </div>
          <div>
            <div className="text-mist mb-1">Member Since</div>
            <div className="text-parchment">{formatDate(user.createdAt)}</div>
          </div>
          <div>
            <div className="text-mist mb-1">Account Age</div>
            <div className="text-parchment">{accountAgeDays} days</div>
          </div>
          <div>
            <div className="text-mist mb-1">Last Updated</div>
            <div className="text-parchment">{formatDate(user.updatedAt)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== WORLDS TAB ==========
function WorldsTab({ user, onUpdate }: { user: AdminUserDetail; onUpdate: () => Promise<void> }) {
  const totalLetters = user.worlds.reduce(
    (sum, w) => sum + w.lettersInCount + w.lettersOutCount,
    0,
  );

  const activeWorlds = user.worlds.filter((w) => {
    if (!w.lastInboundAt) return false;
    const daysSinceActivity = Math.floor(
      (Date.now() - new Date(w.lastInboundAt).getTime()) / (1000 * 60 * 60 * 24),
    );
    return daysSinceActivity < 7;
  }).length;

  return (
    <div className="space-y-6">
      <WorldImportCard userId={user.id} onImport={onUpdate} />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          <div className="text-mist text-sm mb-1">Total Worlds</div>
          <div className="text-parchment text-2xl font-display">{user.worlds.length}</div>
        </div>
        <div className="bg-night border border-success/30 rounded-xl p-4">
          <div className="text-mist text-sm mb-1">Active (7d)</div>
          <div className="text-parchment text-2xl font-display">{activeWorlds}</div>
        </div>
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          <div className="text-mist text-sm mb-1">Inactive</div>
          <div className="text-parchment text-2xl font-display">
            {user.worlds.length - activeWorlds}
          </div>
        </div>
        <div className="bg-night border border-rune/30 rounded-xl p-4">
          <div className="text-mist text-sm mb-1">Total Letters</div>
          <div className="text-parchment text-2xl font-display">{totalLetters}</div>
        </div>
      </div>

      {/* Worlds Grid */}
      {user.worlds.length === 0 ? (
        <div className="bg-night border border-stone/30 rounded-xl p-12 text-center">
          <Globe size={48} className="mx-auto mb-4 text-mist/30" />
          <h3 className="text-parchment text-lg font-medium mb-2">No worlds created</h3>
          <p className="text-mist text-sm">This user hasn't created any worlds yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {user.worlds.map((world) => {
            const totalLettersWorld = world.lettersInCount + world.lettersOutCount;
            const inRatio =
              totalLettersWorld > 0 ? (world.lettersInCount / totalLettersWorld) * 100 : 50;
            const daysSinceActivity = world.lastInboundAt
              ? Math.floor(
                  (Date.now() - new Date(world.lastInboundAt).getTime()) / (1000 * 60 * 60 * 24),
                )
              : null;

            return (
              <div
                key={world.id}
                className="bg-night border border-stone/30 rounded-xl p-4 hover:border-stone/50 transition-colors"
              >
                <Link
                  to={`/admin/worlds/${world.id}`}
                  className="text-rune hover:text-rune-glow hover:underline font-mono text-sm block mb-3"
                >
                  {world.inboundAddress}
                </Link>

                <div className="space-y-2 text-xs text-mist">
                  <div className="flex justify-between">
                    <span>Day {world.dayIndex}</span>
                    <span>{world.timezone}</span>
                  </div>
                  <div>Delivery: {world.deliveryTime}</div>

                  <div className="pt-2 border-t border-stone/20">
                    <div className="flex justify-between mb-1">
                      <span>Letters</span>
                      <span className="text-parchment">
                        {world.lettersInCount} in / {world.lettersOutCount} out
                      </span>
                    </div>
                    {totalLettersWorld > 0 && (
                      <div className="w-full h-1.5 bg-stone/30 rounded-full overflow-hidden">
                        <div className="h-full bg-rune/50" style={{ width: `${inRatio}%` }} />
                      </div>
                    )}
                  </div>

                  <div className="pt-2 border-t border-stone/20">
                    <div className="flex items-center justify-between">
                      <span>Last Activity</span>
                      <span
                        className={
                          daysSinceActivity === null
                            ? ''
                            : daysSinceActivity < 7
                              ? 'text-success'
                              : daysSinceActivity < 30
                                ? 'text-amber-500'
                                : 'text-ember'
                        }
                      >
                        {world.lastInboundAt ? formatRelativeTime(world.lastInboundAt) : 'Never'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WorldImportCard({ userId, onImport }: { userId: string; onImport: () => Promise<void> }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isZip, setIsZip] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const canImport = Boolean((isZip && selectedFile) || (!isZip && payload));

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSuccess(null);
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      setPayload(null);
      setFileName(null);
      setIsZip(false);
      return;
    }
    setSelectedFile(file);
    try {
      const isZipFile =
        file.type === 'application/zip' || file.name.toLowerCase().endsWith('.zip');
      setIsZip(isZipFile);
      if (isZipFile) {
        setPayload(null);
        setFileName(file.name);
        return;
      }
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Import file must be a JSON object');
      }
      setPayload(parsed as Record<string, unknown>);
      setFileName(file.name);
    } catch (err) {
      setPayload(null);
      setIsZip(false);
      setFileName(file.name);
      setError(err instanceof Error ? err.message : 'Invalid JSON file');
    }
  };

  const handleImport = async () => {
    if (!canImport) return;
    if (!confirm('Import this world export and create a new world for this user?')) {
      return;
    }
    setIsImporting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.importWorldForUser(
        userId,
        isZip && selectedFile
          ? (() => {
              const formData = new FormData();
              formData.append('file', selectedFile);
              return formData;
            })()
          : (payload as Record<string, unknown>),
      );
      setSuccess(
        `${result.message} New address: ${result.inboundAddress}.${result.note ? ` ${result.note}` : ''}`,
      );
      setPayload(null);
      setSelectedFile(null);
      setFileName(null);
      setIsZip(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await onImport();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import world');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="bg-night border border-stone/30 rounded-xl p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-rune/20 rounded-lg">
          <Upload size={20} className="text-rune" />
        </div>
        <div>
          <h3 className="font-display text-parchment">Import World</h3>
          <p className="text-mist text-xs mt-1">
            Upload a world export (.zip or .json) to create a new world for this user.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,application/zip"
          onChange={handleFileChange}
          className="block w-full text-sm text-mist file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-stone/30 file:bg-void file:text-parchment hover:file:border-stone/50"
        />
        {fileName && <div className="text-xs text-mist">Selected: {fileName}</div>}
        <button
          onClick={handleImport}
          disabled={isImporting || !canImport}
          className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isImporting ? 'Importing...' : 'Import World'}
        </button>
        {error && <div className="text-ember text-xs">{error}</div>}
        {success && <div className="text-success text-xs">{success}</div>}
      </div>
    </div>
  );
}

// ========== SETTINGS TAB ==========
function SettingsTab({ user, onUpdate }: { user: AdminUserDetail; onUpdate: () => void }) {
  const [quotaValue, setQuotaValue] = useState<number>(user.dailyLetterQuota);
  const [roleValue, setRoleValue] = useState<'user' | 'admin'>(user.role);
  const [statusValue, setStatusValue] = useState<'waitlisted' | 'active' | 'suspended' | 'deleted'>(
    user.status,
  );
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges =
    quotaValue !== user.dailyLetterQuota || roleValue !== user.role || statusValue !== user.status;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await adminApi.updateUser(user.id, {
        dailyLetterQuota: quotaValue,
        role: roleValue,
        status: statusValue,
      });
      alert('Settings updated successfully');
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-night border border-stone/30 rounded-xl p-6">
        <h3 className="text-lg font-medium text-parchment mb-4">Account Configuration</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-mist mb-2">Email</label>
            <div className="text-parchment">{user.email}</div>
          </div>

          <div>
            <label className="block text-sm text-mist mb-2">Verified Sender Email</label>
            <div className="text-parchment">{user.verifiedSenderEmail ?? 'None'}</div>
          </div>

          <div>
            <label className="block text-sm text-mist mb-2">Role</label>
            <select
              value={roleValue}
              onChange={(e) => setRoleValue(e.target.value as 'user' | 'admin')}
              className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-mist mb-2">Status</label>
            <select
              value={statusValue}
              onChange={(e) =>
                setStatusValue(e.target.value as 'waitlisted' | 'active' | 'suspended' | 'deleted')
              }
              className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
            >
              <option value="waitlisted">Waitlisted</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-mist mb-2">Daily Letter Quota</label>
            <input
              type="number"
              min={1}
              max={20}
              value={quotaValue}
              onChange={(e) =>
                setQuotaValue(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))
              }
              className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
            />
            <p className="text-xs text-mist mt-1">Maximum 20 letters per day</p>
          </div>

          <div className="pt-4">
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== DANGER ZONE TAB ==========
function DangerZoneTab({ user, onReset }: { user: AdminUserDetail; onReset: () => void }) {
  const [resetReason, setResetReason] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const handleResetUser = async () => {
    if (!resetReason.trim()) return;

    const worldCount = user.worlds.length;
    const confirmMsg = `This will PERMANENTLY DELETE all ${worldCount} world(s) for ${user.email} and reset their quota to 1.\n\nThis action cannot be undone.\n\nContinue?`;

    if (!confirm(confirmMsg)) return;

    setIsResetting(true);
    try {
      const result = await adminApi.resetUser(user.id, resetReason);
      alert(result.message);
      setResetReason('');
      onReset();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reset user');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-night border border-ember/30 rounded-xl p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle size={24} className="text-ember flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-ember">Danger Zone</h3>
            <p className="text-sm text-mist mt-1">Destructive actions that cannot be undone</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ember mb-2">
              Reset User to New State
            </label>
            <p className="text-xs text-mist mb-3">
              This will permanently delete ALL worlds, letters, and world state data for this user,
              and reset their daily letter quota to 1. This action cannot be undone.
            </p>
            {user.worlds.length > 0 && (
              <p className="text-xs text-ember mb-3">
                Warning: This will delete {user.worlds.length} world
                {user.worlds.length !== 1 ? 's' : ''} and all associated data.
              </p>
            )}
            <input
              type="text"
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              placeholder="Reason for reset (required)..."
              className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-ember mb-3"
            />
            <button
              onClick={handleResetUser}
              disabled={isResetting || !resetReason.trim()}
              className="w-full px-4 py-2 bg-ember/20 text-ember border border-ember/30 rounded-lg text-sm hover:bg-ember/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResetting ? 'Resetting...' : 'Reset User'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper functions
function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}
